import { useCallback, useRef, useState } from 'react'
import { toast } from 'sonner'
import { Badge, Button, Checkbox, Modal } from '@indipia/ui-core'
import {
  sendScheduleChangeNotices,
  type DateChange, type ScheduleChangeNoticeItemResult,
} from '../api/foremanApi'
import { useInvalidateSignals } from '../hooks/useTaskSignals'
import { scheduleChangeNoticeFailMessage } from '../constants/foremanSendMessage'
import { partnerStatusAfterSend, PARTNER_STATUS, type PartnerStatusPatch } from '../constants/partnerStatus'
import { WORK_TYPE_COLORS } from '../views/scheduleUtils'

/** 날짜 저장 응답의 dateChanges 중 확인 창 대상(서버 notifiable 판정 그대로)만 추린다. 0건이면 빈 배열 → 창을 띄우지 않는다. */
export function notifiableDateChanges(dateChanges: DateChange[] | null | undefined): DateChange[] {
  return (dateChanges ?? []).filter(c => c.notifiable)
}

/** 발송 성공 건의 화면 반영값 — 서버 응답(partnerStatus·sentDtm) 기준. 실패 건·sentDtm 없음은 null. */
export function noticeSentPatch(item: ScheduleChangeNoticeItemResult): PartnerStatusPatch | null {
  if (!item.sent || !item.sentDtm) return null
  return partnerStatusAfterSend(item.sentDtm, item.partnerStatus, {
    historyId: item.historyId, attemptNo: item.attemptNo, sendMethod: 'CHANGE', partnerId: item.partnerId, replyExpireDtm: item.replyExpireDtm,
  })
}

/** "M/D ~ M/D", 하루면 "M/D" */
function fmtRange(start: string | null, end: string | null): string {
  const md = (s: string | null) => {
    if (!s) return ''
    const [, m, d] = s.slice(0, 10).split('-')
    return `${Number(m)}/${Number(d)}`
  }
  const a = md(start), b = md(end)
  if (!a) return b
  return !b || a === b ? a : `${a} ~ ${b}`
}

const FALLBACK_COLOR = 'var(--shell-on-surface-2)'

interface Props {
  scheduleId: number
  /** 확인 창 대상(notifiable=true 항목) — 부모가 1건 이상일 때만 마운트한다 */
  changes: DateChange[]
  /** 공종 색 — 부모 화면이 바·점에 쓰는 색 그대로. 없으면 공통 팔레트 순번 */
  colorOf?: (taskId: number, workTypeCode: string) => string | undefined
  /** 발송이 성공한 건(건별 결과)을 부모 공종 목록 state에 반영 — 성공 건마다 한 번에 모아서 호출 */
  onSent: (sentItems: ScheduleChangeNoticeItemResult[]) => void
  onClose: () => void
}

/**
 * 일정 변경 카톡 확인 창 — 날짜를 바꿔 저장한 직후 확정·회신 대기 반장님께 변경 카톡을 보낼지 묻는다.
 * 부모가 조건부 렌더링(`{notice && <ScheduleChangeNoticeModal …/>}`)으로 마운트한다.
 * 실패 건은 창에 남겨 재시도할 수 있고, 성공 건은 목록에서 제거한다. 전부 성공하면 토스트 후 닫는다.
 */
export function ScheduleChangeNoticeModal({ scheduleId, changes, colorOf, onSent, onClose }: Props) {
  const invalidateSignals = useInvalidateSignals()   // 변경 카톡 발송으로 반장 상태가 바뀌면 신호 재조회
  const [rows, setRows] = useState<DateChange[]>(changes)
  const [checked, setChecked] = useState<Set<number>>(() => new Set(changes.map(c => c.taskId)))
  const [sending, setSending] = useState(false)
  const [resultLines, setResultLines] = useState<string[]>([])

  const checkedRows = rows.filter(r => checked.has(r.taskId))
  const n = checkedRows.length

  // ui-core Modal 은 onClose 가 바뀔 때마다 포커스를 다시 잡으므로 참조를 고정한다(보내는 중에는 닫지 않음).
  const sendingRef = useRef(false)
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose
  const close = useCallback(() => { if (!sendingRef.current) onCloseRef.current() }, [])

  const toggle = (taskId: number) => setChecked(prev => {
    const next = new Set(prev)
    if (next.has(taskId)) next.delete(taskId); else next.add(taskId)
    return next
  })

  const handleSend = async () => {
    if (sending || n === 0) return
    sendingRef.current = true
    setSending(true)
    setResultLines([])
    try {
      const res = await sendScheduleChangeNotices(
        scheduleId,
        checkedRows.map(r => ({ taskId: r.taskId, oldStartDate: r.oldStartDate, oldEndDate: r.oldEndDate })),
      )
      const byTask = new Map(res.items.map(it => [it.taskId, it]))
      const sentItems = res.items.filter(it => it.sent)
      if (sentItems.length > 0) { void invalidateSignals(); onSent(sentItems) }

      // 응답에 빠진 건은 실패로 본다(알 수 없는 사유)
      const failed = checkedRows.filter(r => !byTask.get(r.taskId)?.sent)
      if (failed.length === 0) {
        toast.success(`${sentItems.length}명에게 변경 카톡을 보냈어요`)
        sendingRef.current = false
        onCloseRef.current()
        return
      }

      const sentIds = new Set(sentItems.map(it => it.taskId))
      setRows(prev => prev.filter(r => !sentIds.has(r.taskId)))
      setChecked(prev => { const next = new Set(prev); sentIds.forEach(id => next.delete(id)); return next })

      const byMsg = new Map<string, string[]>()
      for (const r of failed) {
        const msg = scheduleChangeNoticeFailMessage(byTask.get(r.taskId)?.failReason)
        byMsg.set(msg, [...(byMsg.get(msg) ?? []), r.workTypeName])
      }
      const lines: string[] = []
      if (sentItems.length > 0) lines.push(`${sentItems.length}명에게 변경 카톡을 보냈어요`)
      byMsg.forEach((names, msg) => lines.push(`${msg} · ${names.join(', ')}`))
      setResultLines(lines)
    } catch {
      setResultLines([`${scheduleChangeNoticeFailMessage(null)} · 다시 시도해 주세요`])
    } finally {
      sendingRef.current = false
      setSending(false)
    }
  }

  return (
    <Modal
      open
      onClose={close}
      title="일정이 바뀐 공종이 있어요"
      footerAlign="stretch"
      footer={<>
        <Button variant="secondary" disabled={sending} onClick={close}>
          이번엔 보내지 않기
        </Button>
        <Button variant="primary" loading={sending} disabled={n === 0} onClick={handleSend}>
          {n === 0 ? '보낼 반장님을 체크해 주세요' : `체크한 ${n}명에게 변경 카톡 보내기`}
        </Button>
      </>}
    >
      <div style={{ fontSize: 'var(--type-body-sm-size)', color: 'var(--shell-on-surface-2)', paddingBottom: 12 }}>
        반장님께 바뀐 일정을 카톡으로 알릴까요?
      </div>

      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 'var(--gap-xs)' }}>
        {rows.map((r, i) => {
          const color = colorOf?.(r.taskId, r.workTypeCode) ?? WORK_TYPE_COLORS[i % WORK_TYPE_COLORS.length] ?? FALLBACK_COLOR
          const confirmed = r.partnerStatus === PARTNER_STATUS.CONFIRMED
          return (
            <li key={r.taskId}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--gap-sm)', padding: '8px 10px', border: 'var(--border-width-base) solid var(--shell-border)', borderRadius: 'var(--radius-md)', cursor: sending ? 'default' : 'pointer' }}>
                <Checkbox bare checked={checked.has(r.taskId)} disabled={sending} onChange={() => toggle(r.taskId)} aria-label={`${r.workTypeName} 선택`} />
                <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0 }} />
                <span style={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <span style={{ fontSize: 'var(--type-body-sm-size)', fontWeight: 700, color: 'var(--shell-on-surface)' }}>
                    {r.workTypeName}
                    <span style={{ fontWeight: 400, color: 'var(--shell-on-surface-2)' }}> · {r.partnerName || '시공파트너'}</span>
                  </span>
                  <span style={{ fontSize: 'var(--type-caption-size)', color: 'var(--shell-on-surface-2)' }}>
                    <s>{fmtRange(r.oldStartDate, r.oldEndDate)}</s>
                    {' → '}
                    <span style={{ color: 'var(--shell-on-surface)', fontWeight: 600 }}>{fmtRange(r.newStartDate, r.newEndDate)}</span>
                  </span>
                </span>
                <Badge variant="soft" status={confirmed ? 'success' : 'warning'}>{confirmed ? '확정' : '회신 대기'}</Badge>
              </label>
            </li>
          )
        })}
      </ul>

      <p style={{ margin: '12px 0 0', fontSize: 'var(--type-caption-size)', color: 'var(--shell-hint)', lineHeight: 1.5 }}>
        확정 · 회신 대기 반장님만 보여요. 보내면 반장님이 다시 답장할 때까지 &apos;재회신 대기&apos;가 돼요.
      </p>

      {resultLines.length > 0 && (
        <div role="alert" style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 2, fontSize: 'var(--type-caption-size)', color: 'var(--status-danger-600)' }}>
          {resultLines.map(line => <div key={line}>{line}</div>)}
        </div>
      )}
    </Modal>
  )
}
